import React, { useEffect, useMemo, useState } from 'react'
import { SerializedStyles, css } from '@emotion/core'
import { Tag } from 'antd'
import { createCustomerTag, deleteCustomerTag, fetchCustomerTagMasters } from '@/apis/aipass'
import { Select, Option } from '@/components/atoms/select'
import { CustomerTagMasterType, tagColors } from '@/models/customer-manager/customer-tag'
import { ReactComponent as RemoveIcon } from '@/static/images/customer-manager/icon_remove.svg'
import '@/components/molecules/customer/ant-select-dropdown.css'

interface CustomerTagInputProps {
  customerTags: string[] | undefined
  customerId: string
  customStyle?: SerializedStyles
  dropdownStyle?: React.CSSProperties
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement
  onDropdownVisibleChange?: (open: boolean) => void
  onBusyChange?: (isBusy: boolean) => void
}

export const CustomerTagInput: React.FC<CustomerTagInputProps> = ({
  customerTags,
  customerId,
  customStyle,
  dropdownStyle,
  getPopupContainer,
  onDropdownVisibleChange,
  onBusyChange,
}) => {
  const [isEdit, setIsEdit] = useState(false)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [customerTagMasters, setCustomerTagMasters] = useState<CustomerTagMasterType[]>([])
  const [loading, setLoading] = useState(false)
  const [isBusy, setIsBusy] = useState(false)

  const fetchMasters = async () => {
    setLoading(true)
    await fetchCustomerTagMasters().then(res => res?.length && setCustomerTagMasters(res))
    setLoading(false)
  }

  const saveCustomerTag = async (tagId: string) => {
    const nextTags = [...selectedTags, tagId]
    setSelectedTags(nextTags)

    try {
      setIsBusy(true)
      await createCustomerTag({ customerId, customerTagMasterIds: nextTags })
    } catch {
      setSelectedTags(selectedTags)
    } finally {
      setIsBusy(false)
    }
  }

  const removeCustomerTag = async (tagId: string) => {
    const prevTags = selectedTags
    setSelectedTags(currentTags => currentTags.filter(tag => tag !== tagId))

    try {
      setIsBusy(true)
      await deleteCustomerTag({ customerId, customerTagMasterId: tagId })
    } catch {
      setSelectedTags(prevTags)
    } finally {
      setIsBusy(false)
    }
  }

  const selectedTagStyle = useMemo(() => {
    const colorStyle = Object.keys(tagColors).reduce<Record<string, unknown>>((acc, colorName) => {
      const tagStyle = {
        background: `${tagColors[colorName].bgColor} !important`,
        color: `${tagColors[colorName].fontColor} !important`,
        svg: {
          fill: `${tagColors[colorName].fontColor} !important`,
        },
        '.custom-select .ant-select-item-option-active:not(.ant-select-item-option-disabled)': {
          color: '#bae7ff !important',
        },
      }
      acc[`.ant-select-selection__choice[title=${colorName}]`] = tagStyle
      return acc
    }, {})

    return css(
      {
        ...colorStyle,
        flex: 1,
        paddingBottom: 0,
        '.ant-select-selection__choice': {
          padding: '0 10px',
          ':not(:has(.no-edit))': {
            '.ant-select-selection__choice__content': {
              marginRight: isEdit ? 16 : 0,
            },
            '.ant-select-selection__choice__remove': {
              display: isEdit ? 'inline-block' : 'none',
            },
          },
          ':has(.no-edit)': {
            '.ant-select-selection__choice__remove': {
              display: 'none',
            },
          },
        },
        '.ant-select-selection__choice__content': {
          fontSize: 12,
          fontWeight: 'bold',
        },
        '.ant-select-selection--multiple': {
          '.ant-select-selection__rendered': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
        },
        '.tag-select': {
          '> div': { borderRadius: 10, minHeight: 62, padding: 8, width: '100%' },
        },
      },
      customStyle,
    )
  }, [customStyle, isEdit])

  useEffect(() => {
    fetchMasters()
  }, [])

  useEffect(() => {
    setSelectedTags(customerTags ?? [])
  }, [customerTags])

  useEffect(() => {
    onBusyChange?.(isBusy)
  }, [isBusy, onBusyChange])

  if (loading) {
    return <></>
  }

  return (
    <Select
      className="tag-select"
      value={selectedTags}
      onFocus={() => setIsEdit(true)}
      onBlur={() => setIsEdit(false)}
      customStyle={selectedTagStyle}
      menuItemSelectedIcon={<img src={require('@/static/images/customer-manager/icon_check.svg')} />}
      mode="multiple"
      dropdownStyle={dropdownStyle}
      getPopupContainer={getPopupContainer}
      onDeselect={value => removeCustomerTag(value as string)}
      onDropdownVisibleChange={onDropdownVisibleChange}
      onSelect={value => saveCustomerTag(value as string)}
      removeIcon={<RemoveIcon />}
    >
      {customerTagMasters.map(customerTagMaster => (
        <Option
          key={customerTagMaster.id}
          title={customerTagMaster.tag.colorName}
          value={customerTagMaster.id}
          className={customerTagMaster.isCurrentHotelTag ? '' : 'hidden'}
        >
          <Tag
            color={customerTagMaster.tag.bgColor}
            style={{ color: customerTagMaster.tag.fontColor, marginRight: 0, padding: 0 }}
            className={customerTagMaster.isCurrentHotelTag ? '' : 'no-edit'}
          >
            {customerTagMaster.tag.name}
          </Tag>
        </Option>
      ))}
    </Select>
  )
}
